/**
 * 訪談 Modal（REQ-20260418015）。
 * 取代原本在 InterviewTimeline 內直接展開 inline form 的流程。
 *
 * 兩種模式：
 * - mode='new'：建立訪談；附件透過 PendingAttachmentsPicker 一次性收集，訪談建立後批次上傳
 * - mode='edit'：更新訪談；附件沿用既有 AttachmentUploader inline 上傳（edit 模式下訪談已有 id）
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  CreateInterviewInput,
  InterviewResponse,
} from '@app/shared';
import { Modal } from '../../components/Modal';
import { InterviewForm } from './InterviewForm';
import {
  useCreateInterview,
  useDeleteInterview,
  useUpdateInterview,
  type InterviewOwnerType,
} from './api';
import { PendingAttachmentsPicker } from '../attachments/PendingAttachmentsPicker';
import { AttachmentUploader } from '../attachments/AttachmentUploader';
import { AttachmentList } from '../attachments/AttachmentList';
import { uploadAttachment } from '../attachments/api';
import { getApiErrorMessage } from '../../lib/api-client';

function countDone(progress: Record<string, number | 'done' | string>): number {
  return Object.values(progress).filter((v) => v === 'done').length;
}

export interface InterviewModalProps {
  open: boolean;
  onClose: () => void;
  /** 客戶 id 或廠商 id(由 ownerType 決定) */
  ownerId: number;
  ownerType: InterviewOwnerType;
  mode: 'new' | 'edit';
  interview?: InterviewResponse | null;
  /**
   * REQ-20260421008 §2:新增訪談時自動帶入 owner 主檔的預設聯絡人
   * (客戶 = customer.contact;廠商 = supplier.boss/contact1)。
   * 編輯模式忽略此 prop,沿用訪談自身的 contact。
   */
  defaultContact?: string | null;
  /**
   * 新增模式下複製來源訪談的「類別」與「Notes」做為預填,
   * 簡化複製類似訪談的輸入。其它欄位(時間 / 聯絡人 / 附件)維持新增預設行為。
   */
  copyFrom?: InterviewResponse | null;
}

export function InterviewModal(props: InterviewModalProps): JSX.Element {
  const { open, onClose, ownerId, ownerType, mode, interview, defaultContact, copyFrom } = props;
  const qc = useQueryClient();
  const createMutation = useCreateInterview(ownerId, ownerType);
  const updateMutation = useUpdateInterview(ownerId, ownerType);
  const deleteMutation = useDeleteInterview(ownerId, ownerType);

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<
    Record<string, number | 'done' | string>
  >({});
  const [uploading, setUploading] = useState(false);

  const reset = (): void => {
    setPendingFiles([]);
    setUploadProgress({});
    setUploading(false);
  };

  const handleClose = (): void => {
    if (uploading) return;
    reset();
    onClose();
  };

  const handleCreate = (values: CreateInterviewInput): void => {
    createMutation.mutate(values, {
      onSuccess: async (created) => {
        if (pendingFiles.length === 0) {
          reset();
          onClose();
          return;
        }
        setUploading(true);
        let anyError = false;
        for (const file of pendingFiles) {
          try {
            await uploadAttachment(
              created.id,
              file,
              (pct) => {
                setUploadProgress((prev) => ({ ...prev, [file.name]: pct }));
              },
              ownerType,
            );
            setUploadProgress((prev) => ({ ...prev, [file.name]: 'done' }));
          } catch (err) {
            anyError = true;
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: getApiErrorMessage(err, '上傳失敗'),
            }));
          }
        }
        void qc.invalidateQueries({
          queryKey: ['interviews', ownerType, ownerId],
          exact: false,
        });
        setUploading(false);
        if (!anyError) {
          reset();
          onClose();
        }
      },
    });
  };

  const handleUpdate = (values: CreateInterviewInput): void => {
    if (!interview) return;
    updateMutation.mutate(
      { id: interview.id, input: values },
      { onSuccess: () => onClose() },
    );
  };

  const handleDelete = (): void => {
    if (!interview) return;
    // REQ-20260909003:訪談刪掉但它建出來的應收單還在,那張單就變成沒有來源的帳款。
    // 後端才是權威(softDelete 會擋下並回 400),這裡只是提前告知,
    // 免得使用者按下去才吃到 400。廠商訪談沒有這個欄位(undefined)。
    const blockingOrders = interview.receivableOrders ?? [];
    if (blockingOrders.length > 0) {
      const orderNos = blockingOrders.map((o) => o.orderNo).join('、');
      // eslint-disable-next-line no-alert
      window.alert(
        `這筆訪談已建立應收單 ${orderNos}，請先將該單作廢（草稿單可直接刪除）後再刪除訪談`,
      );
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm('確定刪除這筆訪談？')) return;
    deleteMutation.mutate(interview.id, {
      onSuccess: () => onClose(),
    });
  };

  const title = mode === 'new' ? '新增訪談' : '編輯訪談';

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      dismissOnBackdrop={!uploading}
      widthClass="max-w-3xl"
    >
      <div className="grid gap-4">
        {createMutation.isError ? (
          <div role="alert" className="bg-red-50 text-red-700 text-sm p-2 rounded">
            建立失敗：
            {getApiErrorMessage(createMutation.error, '未知錯誤')}
          </div>
        ) : null}
        {updateMutation.isError ? (
          <div role="alert" className="bg-red-50 text-red-700 text-sm p-2 rounded">
            更新失敗：
            {getApiErrorMessage(updateMutation.error, '未知錯誤')}
          </div>
        ) : null}

        {mode === 'new' ? (
          <>
            <InterviewForm
              // REQ-20260421008 §2, §3:新增時預填 owner 主檔的聯絡人 + 此刻時間
              // copyFrom:複製來源訪談時加帶類別 + Notes(其餘維持新增預設)
              defaultValues={{
                contact: defaultContact ?? null,
                visitedAt: new Date().toISOString(),
                ...(copyFrom
                  ? {
                      interviewTypeId: copyFrom.interviewTypeId ?? null,
                      notes: copyFrom.notes ?? null,
                    }
                  : {}),
              }}
              // 私帳只有客戶訪談有(REQ-20260810002);廠商訪談沒有這個欄位
              showPrivateAccount={ownerType === 'customer'}
              submitting={createMutation.isPending || uploading}
              submitLabel={
                uploading
                  ? `上傳中（${countDone(uploadProgress)}/${pendingFiles.length}）`
                  : '建立'
              }
              {...(uploading ? {} : { onCancel: handleClose })}
              onSubmit={handleCreate}
            />
            <div className="bg-slate-50 border rounded-md p-3 grid gap-2">
              <div className="text-sm font-medium text-slate-700">附件（選填）</div>
              <PendingAttachmentsPicker
                files={pendingFiles}
                onChange={setPendingFiles}
                progress={uploadProgress}
                disabled={uploading}
              />
            </div>
          </>
        ) : interview ? (
          <>
            <InterviewForm
              defaultValues={interview}
              showPrivateAccount={ownerType === 'customer'}
              submitting={updateMutation.isPending}
              submitLabel="更新"
              // REQ-20260825009:沒改任何欄位時「更新」鍵維持灰框,一有異動才轉藍
              colorByDirty
              onCancel={handleClose}
              onSubmit={handleUpdate}
            />
            <div className="bg-slate-50 border rounded-md p-3 grid gap-2">
              <div className="text-sm font-medium text-slate-700">附件</div>
              <AttachmentList
                attachments={interview.attachments}
                canWrite={true}
                ownerId={ownerId}
                ownerType={ownerType}
                interviewId={interview.id}
              />
              <AttachmentUploader
                interviewId={interview.id}
                ownerId={ownerId}
                ownerType={ownerType}
              />
            </div>
            {deleteMutation.isError ? (
              <div role="alert" className="bg-red-50 text-red-700 text-sm p-2 rounded">
                刪除失敗：
                {getApiErrorMessage(deleteMutation.error, '未知錯誤')}
              </div>
            ) : null}
            <div className="border-t pt-3 flex justify-end">
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="text-sm border rounded px-3 py-1.5 text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-50"
                data-testid="btn-delete-interview"
              >
                {deleteMutation.isPending ? '刪除中…' : '刪除這筆訪談'}
              </button>
            </div>
          </>
        ) : (
          <div className="text-slate-500">無訪談資料</div>
        )}
      </div>
    </Modal>
  );
}
